import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import './Auth.css';

function Auth() {
  const [isLogin, setIsLogin] = useState(true);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showConfirmationMessage, setShowConfirmationMessage] = useState(false);

  // Check if user is being redirected from email confirmation
  useEffect(() => {
    const handleEmailConfirmation = async () => {
      // Check for confirmation token in URL hash
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const accessToken = hashParams.get('access_token');
      const type = hashParams.get('type');
      
      if (accessToken && type === 'signup') {
        // User clicked email confirmation link
        try {
          const { data, error } = await supabase.auth.getSession();
          
          if (!error && data.session) {
            // Email confirmed and user is logged in
            setSuccess('Email confirmed successfully! Redirecting...');
            setTimeout(() => {
              window.location.href = '/';
            }, 2000);
          }
        } catch (err) {
          setError('Error confirming email. Please try again.');
        }
      }
    };

    handleEmailConfirmation();
  }, []);

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      if (!email) {
        throw new Error('Please enter your email address');
      }

      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;

      setSuccess('Password reset email sent! Check your inbox.');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError(null);
    setLoading(true);

    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/`,
        },
      });

      if (error) throw error;
      // The redirect will happen automatically
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      if (isLogin) {
        // Login
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (error) throw error;

        // Success - redirect handled by parent or we can redirect here
        setSuccess('Login successful! Redirecting...');
        setTimeout(() => {
          window.location.href = '/';
        }, 1500);
      } else {
        // Signup
        if (password !== confirmPassword) {
          throw new Error('Passwords do not match');
        }

        if (password.length < 6) {
          throw new Error('Password must be at least 6 characters');
        }

        const { data, error } = await supabase.auth.signUp({
          email,
          password,
        });

        if (error) throw error;

        // Success - but user needs to confirm email
        if (data.user && !data.session) {
          setSuccess('Account created! Please check your email to confirm your account before signing in.');
          setShowConfirmationMessage(true);
          // Clear form
          setEmail('');
          setPassword('');
          setConfirmPassword('');
        } else if (data.session) {
          // If session exists (confirmation not required or already confirmed)
          setSuccess('Account created successfully! Redirecting...');
          setTimeout(() => {
            window.location.href = '/';
          }, 1500);
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // If showing confirmation message, show simple dialog
  if (showConfirmationMessage) {
    return (
      <div className="auth-container">
        <div className="confirmation-dialog">
          <div className="confirmation-icon">✉️</div>
          <h1>Check Your Email</h1>
          <p className="confirmation-text">
            We've sent a confirmation email to <strong>{email}</strong>
          </p>
          <p className="confirmation-text">
            Please click the confirmation link in the email to activate your account.
          </p>
          <p className="confirmation-text-small">
            After confirming, you'll be automatically logged in and redirected to the home page.
          </p>
          <div className="confirmation-help-box">
            <p><strong>Didn't receive the email?</strong></p>
            <p>Check your spam folder or try signing up again.</p>
          </div>
          <button
            className="link-button"
            onClick={() => {
              setShowConfirmationMessage(false);
              setSuccess(null);
              setEmail('');
            }}
          >
            Back to Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <button
            className={`auth-toggle ${isLogin ? 'active' : ''}`}
            onClick={() => {
              setIsLogin(true);
              setShowForgotPassword(false);
              setShowConfirmationMessage(false);
              setError(null);
              setSuccess(null);
            }}
          >
            Login
          </button>
          <button
            className={`auth-toggle ${!isLogin ? 'active' : ''}`}
            onClick={() => {
              setIsLogin(false);
              setShowForgotPassword(false);
              setShowConfirmationMessage(false);
              setError(null);
              setSuccess(null);
            }}
          >
            Sign Up
          </button>
        </div>

        <form onSubmit={showForgotPassword ? handleForgotPassword : handleAuth} className="auth-form">
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
              disabled={loading}
            />
          </div>

          {!showForgotPassword && (
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                disabled={loading}
                minLength={6}
              />
            </div>
          )}

          {!isLogin && (
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your password"
                required
                disabled={loading}
                minLength={6}
              />
            </div>
          )}

          {isLogin && !showForgotPassword && (
            <div className="forgot-password-link">
              <button
                type="button"
                className="link-button"
                onClick={() => {
                  setShowForgotPassword(true);
                  setError(null);
                  setSuccess(null);
                }}
              >
                Forgot password?
              </button>
            </div>
          )}

          {error && <div className="error-message">{error}</div>}
          {success && !showConfirmationMessage && (
            <div className="success-message">
              {success}
            </div>
          )}

          {!showForgotPassword ? (
            <>
              <button type="submit" className="auth-button" disabled={loading}>
                {loading ? 'Processing...' : isLogin ? 'Login' : 'Sign Up'}
              </button>

              <div className="divider">
                <span>or</span>
              </div>

              <button
                type="button"
                className="google-button"
                onClick={handleGoogleSignIn}
                disabled={loading}
              >
                <svg className="google-icon" viewBox="0 0 24 24" width="20" height="20">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                {loading ? 'Connecting...' : 'Continue with Google'}
              </button>
            </>
          ) : (
            <div className="forgot-password-actions">
              <button
                type="button"
                className="auth-button"
                onClick={handleForgotPassword}
                disabled={loading}
              >
                {loading ? 'Sending...' : 'Send Reset Link'}
              </button>
              <button
                type="button"
                className="link-button"
                onClick={() => {
                  setShowForgotPassword(false);
                  setError(null);
                  setSuccess(null);
                }}
                disabled={loading}
              >
                Back to Login
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}

export default Auth;

